import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { ConfigService } from '../config/config.service';
import { CategoriesService } from '../categories/categories.service';
import { DepartmentsService } from '../departments/departments.service';
import { SnackbarService } from '../utils/snackbar.service';
import { AuthenticationService } from '../authentication';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { Cart } from '../../../shared/models/cart.model';
import { DepartmentWithResidents } from '../../../shared/models/department.model';
import { Package } from '../../../shared/models/package.model';
import { Action, ResponseStatus } from '../../types/enums';
import { BackendResponse } from '../../types/interfaces';

/**
 * Data-fetching half of the cart. Talks to the backend (or to local storage
 * when a test customer is logged in) and knows nothing about the cart that is
 * currently being edited — every operation takes its input as an argument and
 * returns the server answer. Cart state lives in `CartStateService`.
 */
@Injectable({ providedIn: 'root' })
export class CartApiService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private categoriesService: CategoriesService,
    private departmentsService: DepartmentsService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private authService: AuthenticationService,
    private testCustomerService: TestCustomerService
  ) {}

  saveCart(
    cart: Cart,
    action: Action
  ): Observable<BackendResponse<{ id: number }>> {
    if (this.authService.loggedInAsTestCustomer()) {
      this.testCustomerService.saveCart(cart);
      return of({
        data: { id: 0 },
        message: 'Test customer cart saved in local storage',
        status: ResponseStatus.Success,
      });
    }

    const convertedCart = cart.convertCart(this.categoriesService.categories);
    const url = this.configService.endpoints.cart.cartUrl;

    return this.departmentsService
      .getDepartmentWithResidents(convertedCart.departmentId)
      .pipe(
        switchMap(res => {
          this.checkResidentDepartmentId(res.data, cart);
          return this.http.post<BackendResponse<{ id: number }>>(url, {
            cart: convertedCart,
            action: action,
          });
        }),
        catchError(error => this.handleError(error, 'cart.error.inCartSaving'))
      );
  }

  fetchCart(departmentId: number): Observable<BackendResponse<Cart>> {
    if (this.authService.loggedInAsTestCustomer()) {
      return of({
        data: this.testCustomerService.getCart(departmentId),
        message: 'Test customer cart loaded from local storage',
        status: ResponseStatus.Success,
      });
    }

    const url = this.configService.endpoints.cart.cartUrl;
    return this.http
      .get<
        BackendResponse<Cart>
      >(url, { params: { departmentId: departmentId.toString() } })
      .pipe(
        map((arg: any) => deserializeResponse<Cart>(arg, Cart)),
        catchError(error =>
          this.handleError(error, 'cart.error.inCartRetrieving')
        )
      );
  }

  fetchCustomerCarts(): Observable<BackendResponse<Cart[]>> {
    if (
      this.authService.loggedInAsTestCustomer() &&
      this.testCustomerService.hasCarts()
    ) {
      return of({
        data: this.testCustomerService.getCarts(),
        message: 'Test customer carts loaded from local storage',
        status: ResponseStatus.Success,
      });
    }

    const url = this.configService.endpoints.cart.customerCartsUrl;
    return this.http
      .get<BackendResponse<Cart[]>>(url)
      .pipe(map((arg: any) => deserializeResponse<Cart[]>(arg, Cart)));
  }

  getPackagingPrice(cartId: number): Observable<BackendResponse<Package[]>> {
    if (this.authService.loggedInAsTestCustomer()) {
      return of({
        data: this.testCustomerService.getPackagesCosts(cartId),
        message: 'Test customer packages costs loaded from local storage',
        status: ResponseStatus.Success,
      });
    }

    const url = replaceUrlParams(
      this.configService.endpoints.cart.getPackagesUrl,
      ['cartId'],
      [cartId]
    );
    return this.http.get<BackendResponse<Package[]>>(url);
  }

  private handleError(error: any, translationKey: string): Observable<never> {
    if (error?.isHandled) return EMPTY;
    this.snackbarService.error(this.translateService.instant(translationKey));
    return throwError(() => error);
  }

  private checkResidentDepartmentId(
    department: DepartmentWithResidents,
    cart: Cart
  ): void {
    const missingResidents = [];
    for (const residentCart of cart.residentCarts) {
      if (residentCart.products && residentCart.products.length) {
        const isResidentExistsInDepartment = department.people.find(
          p => p.id === residentCart.residentId
        );
        if (isResidentExistsInDepartment === undefined) {
          missingResidents.push(residentCart.residentId);
        }
      }
    }
    if (missingResidents.length > 0) {
      console.log('error');
    }
  }
}
